import React from 'react';
import { Space, Tag, Button, Popconfirm, Typography } from 'antd';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  DisconnectOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import type { MyOrganiserCompetition } from '../types';

const { Text } = Typography;

function formatHeartbeat(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function agentStatusTag(c: MyOrganiserCompetition): React.ReactNode {
  if (c.agent_online) {
    return (
      <Tag icon={<CheckCircleOutlined />} color="success">
        Live
      </Tag>
    );
  }
  if (c.last_agent_heartbeat_at) {
    return (
      <Tag icon={<DisconnectOutlined />} color="default">
        Offline
      </Tag>
    );
  }
  return (
    <Tag icon={<QuestionCircleOutlined />} color="warning">
      No agent yet
    </Tag>
  );
}

function competitionColumn(): ColumnType<MyOrganiserCompetition> {
  return {
    title: 'Competition',
    dataIndex: 'name',
    key: 'name',
    width: 200,
    render: (name: string, row) => (
      <Space direction="vertical" size={0} style={{ paddingLeft: 12 }}>
        <Text strong style={{ wordBreak: 'break-word' }}>
          {name}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {String(row.start_date || '—').slice(0, 10)} → {String(row.end_date || '—').slice(0, 10)}
        </Text>
      </Space>
    ),
  };
}

function meetColumn(): ColumnType<MyOrganiserCompetition> {
  return {
    title: 'Meet',
    key: 'meet',
    width: 96,
    render: (_, row) => (row.is_active ? <Tag color="green">Active</Tag> : <Tag>Archived</Tag>),
  };
}

function agentColumn(): ColumnType<MyOrganiserCompetition> {
  return {
    title: 'Agent',
    key: 'agent',
    width: 124,
    render: (_, row) => agentStatusTag(row),
  };
}

function heartbeatColumn(): ColumnType<MyOrganiserCompetition> {
  return {
    title: 'Last check-in',
    key: 'hb',
    width: 168,
    render: (_, row) => (
      <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        {formatHeartbeat(row.last_agent_heartbeat_at)}
      </Text>
    ),
  };
}

function pathsColumn(): ColumnType<MyOrganiserCompetition> {
  return {
    title: 'Paths (reference)',
    key: 'paths',
    ellipsis: true,
    responsive: ['md'],
    render: (_, row) => (
      <Text type="secondary" style={{ fontSize: 12, wordBreak: 'break-word' }}>
        In: {row.input_folder || '—'}
        <br />
        Out: {row.output_folder || '—'}
      </Text>
    ),
  };
}

function actionsColumn(onArchive: (competitionId: number) => void): ColumnType<MyOrganiserCompetition> {
  return {
    title: 'Actions',
    key: 'actions',
    width: 112,
    align: 'center',
    render: (_, row) =>
      row.is_active ? (
        <Popconfirm
          title="Archive this competition?"
          description="It will no longer appear on the public Live page."
          onConfirm={() => onArchive(row.id)}
          okText="Archive"
        >
          <Button size="small" icon={<InboxOutlined />}>
            Archive
          </Button>
        </Popconfirm>
      ) : null,
  };
}

/**
 * Columns for the "Your competitions" table.
 * Archived and agent state are shown in the same row.
 *
 * @param onArchive - Invoked with competition id after archive confirmation
 */
export function getCompetitionOverviewColumns(
  onArchive: (competitionId: number) => void,
): ColumnsType<MyOrganiserCompetition> {
  return [
    competitionColumn(),
    meetColumn(),
    agentColumn(),
    heartbeatColumn(),
    actionsColumn(onArchive),
  ];
}

/**
 * Columns for the "Agent connections (per meet)" table.
 * Focused on heartbeat and server path details.
 */
export function getAgentConnectionsColumns(): ColumnsType<MyOrganiserCompetition> {
  return [
    competitionColumn(),
    agentColumn(),
    heartbeatColumn(),
    pathsColumn(),
  ];
}
